/**
 * Toolbar button for a single catalog command. Spec §5.1.
 *
 * Duplicated from apps/web per spec §6.2 (ESLint boundary blocks cross-app
 * imports). Keep in sync with the customer copy.
 *
 * Renders the lucide icon, the translated label (as tooltip on hover), and
 * a spinner when the command is dispatching. Variant comes from the catalog
 * entry (`destructive`) OR the layout override (`MDM_ACTION_LAYOUT[id].variant`).
 */
import { Loader2 } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipTrigger, cn } from '@staff-app/ui';
import type { MdmCommandDefinition } from '@staff-app/shared-types';
import { getIcon } from './MdmActionIcons';
import { getLayoutEntry, type MdmActionLayoutEntry, MDM_ACTION_LAYOUT } from './MdmActionLayout';
import type { MdmCommandId } from '@staff-app/shared-types';

interface Props {
  cmd: MdmCommandDefinition;
  isLoading: boolean;
  onClick: () => void;
  layout?: Record<MdmCommandId, MdmActionLayoutEntry>;
  /**
   * When set, the button renders disabled and its tooltip explains why (e.g. a screenshot needs the
   * on-device agent).
   */
  disabledReason?: string | null;
}

export function MdmActionButton({
  cmd,
  isLoading,
  onClick,
  layout = MDM_ACTION_LAYOUT,
  disabledReason,
}: Props) {
  const Icon = getIcon(cmd.id);
  const layoutVariant = getLayoutEntry(cmd.id, layout).variant;
  // scio3 parity: default to `secondary` (soft primary tint + primary text) so the toolbar doesn't
  // shout; `default` (solid) only when the layout asks for it. Destructive commands render as
  // "soft destructive": outline button with red text + border, no solid red fill.
  const isDestructive = layoutVariant === 'destructive' || cmd.destructive;
  const variant: 'default' | 'secondary' | 'outline' = isDestructive
    ? 'outline'
    : layoutVariant === 'default'
      ? 'default'
      : 'secondary';
  // Catalog `i18nKey` / `tooltipKey` are flat English labels with i18n dropped.
  const label = cmd.i18nKey;
  const tooltipText = cmd.tooltipKey ?? label;

  const button = (
    <Button
      variant={variant}
      size="sm"
      className={cn(
        'shrink-0 cursor-pointer gap-1.5',
        isDestructive &&
          'text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
      )}
      disabled={isLoading || !!disabledReason}
      disabledTooltip={disabledReason || undefined}
      onClick={onClick}
      aria-label={label}
      data-testid={`mdm-action-${cmd.id}`}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <Icon className="h-4 w-4" aria-hidden />
      )}
      <span>{label}</span>
    </Button>
  );

  // Disabled-with-a-reason: the shared Button renders its own tooltip on a wrapper span (a disabled
  // button fires no pointer events, so the outer Tooltip could never open).
  if (disabledReason) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom">{tooltipText}</TooltipContent>
    </Tooltip>
  );
}
